import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button, { ButtonType } from '../../general-components/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPlus,
    faPlusSquare,
    faStickyNote,
} from '@fortawesome/free-solid-svg-icons';
import styles from './ImplantNoteComponent.module.css';
import {
    createImplantNote,
    getImplantNotes,
    updateImplantNote,
    deleteImplantNote,
} from '../../../services/api/implantNoteService';
import EditLog from '../../protocols/EditLog/EditLog';
import ConfirmationModal from '../../general/modal/ConfirmationModal';
import AuthContext from '../../../store/auth-context';
import NoteAltIcon from '@mui/icons-material/NoteAlt';
import ConfirmActionButton from '../../general/confirmation/ConfirmActionButton';
import ConditionalsTable from '../conditionals/ConditionalsTable';
import FormInput from '../../../components/general-components/FormInput';
import RichText from '../../../components/general-components/RichText';
import BackgroundContainer from '../../../components/ui-components/BackgroundContainer';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import FileSelector from '../../files/FileSelector';
import FileLink from '../../files/FileLink';
import FileModal from '../../video-overview/FileModal';
import PERMISSION_KEYS from '../../../constants/permission-keys';

const ImplantNoteComponent: React.FC<{ implantId: string }> = ({
    implantId,
}) => {
    const { t } = useTranslation();
    const ctx = useContext(AuthContext);
    const context = ctx.getContext();

    const [showModal, setShowModal] = useState(false);
    const [notes, setNotes] = useState<any[]>([]);
    const [currentNote, setCurrentNote] = useState<any>(null);

    const [title, setTitle] = useState('');
    const [note, setNote] = useState('');
    const [editComment, setEditComment] = useState('');
    const [attachedFiles, setAttachedFiles] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [shownFile, setShownFile] = useState<any>(null);
    const isMobile = useMediaQuery('(max-width: 768px)');

    const toggleModal = (noteToEdit: any = null) => {
        if (noteToEdit) {
            setCurrentNote(noteToEdit);
            setTitle(noteToEdit.title);
            setNote(noteToEdit.note);
            setAttachedFiles(
                noteToEdit.attachedFiles?.map((file: any) => file._id) || []
            );
        } else {
            setCurrentNote(null);
            setTitle('');
            setNote('');
            setAttachedFiles([]);
        }
        setShowModal(!showModal);
    };

    const saveNote = async () => {
        setIsLoading(true);
        if (currentNote) {
            // Update existing note
            await updateImplantNote(
                currentNote._id,
                title,
                note,
                editComment,
                attachedFiles
            );
        } else {
            // Create new note
            await createImplantNote(implantId, title, note, attachedFiles);
        }
        await fetchNotes();
        toggleModal();
        setIsLoading(false);
    };

    const removeNote = async (noteId: string) => {
        setIsLoading(true);
        await deleteImplantNote(noteId);
        await fetchNotes();
        setIsLoading(false);
    };

    const fetchNotes = async () => {
        const notesData = await getImplantNotes(implantId);
        setNotes(notesData);
    };

    // Handle file selection from FileSelector
    const handleFilesSelected = (fileIds: string[]) => {
        setAttachedFiles(fileIds);
    };

    useEffect(() => {
        fetchNotes();
    }, [implantId]);

    return (
        <>
            {showModal && (
                <ConfirmationModal
                    title={
                        currentNote
                            ? t('implantNote.modal.editTitle')
                            : t('implantNote.modal.addTitle')
                    }
                    message={
                        currentNote
                            ? t('implantNote.modal.editMessage')
                            : t('implantNote.modal.addMessage')
                    }
                    onClose={() => toggleModal()}
                    confirmText={
                        currentNote
                            ? t('implantNote.modal.saveButton')
                            : t('implantNote.modal.addButton')
                    }
                    cancelText={t('implantNote.modal.cancelButton')}
                    onConfirm={saveNote}
                    wide={true}
                >
                    <div className={styles.form}>
                        <FormInput
                            label={t('implantNote.form.title')}
                            type="text"
                            defaultValue={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder={t('implantNote.form.titlePlaceholder')}
                        />
                    </div>
                    <RichText
                        white
                        label={t('implantNote.form.note')}
                        description={note}
                        onChange={(val) => setNote(val)}
                    ></RichText>

                    <FileSelector
                        onFilesSelected={handleFilesSelected}
                        defaultFiles={currentNote?.attachedFiles?.map((file: any) => file._id) || []}
                        ownerType="department"
                        allowMultiple={true}
                        allowedTypes={{
                            document: true,
                            image: true,
                            video: false,
                        }}
                        label={t('implantNote.form.attachFiles')}
                    />
                </ConfirmationModal>
            )}

            {shownFile && (
                <FileModal
                    file={{ ...shownFile, title: shownFile.filename }}
                    close={() => setShownFile(null)}
                />
            )}
            {(notes.length > 0 ||
                ctx.hasPermission(PERMISSION_KEYS.manageNotes)) && (
                <BackgroundContainer>
                    <h4>
                        {t('implantNote.sections.conditionalsForDepartment', {
                            departmentName: context.department.name,
                        })}
                    </h4>

                    <div className={styles.notesContainer}>
                        {notes.map((note) => (
                            <div key={note._id} className={styles.noteCard}>
                                <ConditionalsTable
                                    style={'department'}
                                    isInfo={true}
                                    headers={[
                                        <div className={styles.noteTitle}>
                                            {note.title}
                                            <div
                                                className={styles.actionButtons}
                                            >
                                                {!isMobile && (
                                                    <span
                                                        className={
                                                            styles.editLog
                                                        }
                                                    >
                                                        {t(
                                                            'implantNote.text.addedBy',
                                                            {
                                                                name: note
                                                                    .editLog[0]
                                                                    .name,
                                                            }
                                                        )}
                                                    </span>
                                                )}
                                                {ctx.hasPermission(
                                                    PERMISSION_KEYS.manageNotes
                                                ) && (
                                                    <>
                                                        <Button
                                                            small={true}
                                                            type={
                                                                ButtonType.tietary
                                                            }
                                                            onClick={() =>
                                                                toggleModal(
                                                                    note
                                                                )
                                                            }
                                                        >
                                                            {t(
                                                                'implantNote.buttons.edit'
                                                            )}
                                                        </Button>
                                                        <ConfirmActionButton
                                                            buttonType={
                                                                ButtonType.cancel
                                                            }
                                                            onConfirm={() =>
                                                                removeNote(
                                                                    note._id
                                                                )
                                                            }
                                                            modalInfo={{
                                                                title: t(
                                                                    'implantNote.deleteModal.title'
                                                                ),
                                                                message: t(
                                                                    'implantNote.deleteModal.message'
                                                                ),
                                                                submitButtonDanger:
                                                                    true,
                                                                submitButtonText:
                                                                    t(
                                                                        'implantNote.deleteModal.deleteButton'
                                                                    ),
                                                            }}
                                                            buttonText={t(
                                                                'implantNote.buttons.delete'
                                                            )}
                                                        >
                                                            {t(
                                                                'implantNote.deleteModal.warning'
                                                            )}
                                                        </ConfirmActionButton>
                                                    </>
                                                )}
                                            </div>
                                        </div>,
                                    ]}
                                    fields={[
                                        [
                                            <>
                                                <div
                                                    dangerouslySetInnerHTML={{
                                                        __html: note.note,
                                                    }}
                                                />

                                                {note.attachedFiles &&
                                                    note.attachedFiles.length >
                                                        0 && (
                                                        <div
                                                            className={
                                                                styles.attachedFiles
                                                            }
                                                        >
                                                            <h6>
                                                                {t(
                                                                    'implantNote.sections.attachedFiles'
                                                                )}
                                                            </h6>
                                                            {note.attachedFiles.map(
                                                                (file: any) => (
                                                                    <FileLink
                                                                        key={
                                                                            file._id
                                                                        }
                                                                        file={
                                                                            file
                                                                        }
                                                                        onClick={
                                                                            setShownFile
                                                                        }
                                                                    />
                                                                )
                                                            )}
                                                        </div>
                                                    )}
                                            </>,
                                        ],
                                    ]}
                                />
                            </div>
                        ))}
                        {ctx.hasPermission(PERMISSION_KEYS.manageNotes) && (
                            <div className={styles.addNoteButtonContainer}>
                                <Button
                                    className={styles.addNoteButton}
                                    type={ButtonType.primary}
                                    onClick={() => toggleModal()}
                                >
                                    <FontAwesomeIcon icon={faPlus} />
                                </Button>
                            </div>
                        )}
                    </div>
                </BackgroundContainer>
            )}
        </>
    );
};

export default ImplantNoteComponent;
